// client\src\dashboard\components\ToastConfirm.js
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./toast.css";
import "../styles/tutor-dashboard-cards.css";
/**
 * Режимы:
 * - position="inline" — для инлайновых уведомлений/confirm в контейнере
 * - position="fixed"  — тост (по умолчанию фикс к вьюпорту). Можно отключить портал и
 *   передать style={{ position: "absolute", ... }} — тогда тост будет внутри родителя.
 *
 * Если standalone = true, компонент слушает window.addEventListener(eventName)
 * и показывает себя при window.dispatchEvent(new CustomEvent(eventName, { detail: {...} })).
 */
export default function ToastConfirm({
  show = false,
  kind = "info", // info | success | warn | error | confirm
  headline = "",
  title = "",
  text = "",
  icon = null,
  onYes,
  onNo,
  onClose,

  position = "fixed", // "inline" | "fixed"
  standalone = false,
  eventName = "toast:show",
  timeout = 2600,
  autoClose = true,
  showButton = true,
  buttonText = "Ок",

  usePortal = true,
  style = null,
}) {
  const [self, setSelf] = useState({
    show: false,
    kind: null, // !!! раньше было "info" и оно всегда перекрывало проп kind
    title: "",
    text: "",
    onYes: null,
    onNo: null,
  });
  const timerRef = useRef(null);

  // слушатель standalone-тостов
  useEffect(() => {
    if (!standalone) return;

    const handler = (e) => {
      const d = e?.detail || {};
      const k = d.type || "success";
      const isConfirm = k === "confirm";

      setSelf({
        show: true,
        kind: k,
        title: d.title || "",
        text: d.text || "",
        onYes: isConfirm && typeof d.onYes === "function" ? d.onYes : null,
        onNo: isConfirm && typeof d.onNo === "function" ? d.onNo : null,
        showButton: d.showButton,
      });

      if (autoClose && !isConfirm) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          setSelf((s) => ({ ...s, show: false }));
          timerRef.current = null;
          onClose?.();
        }, d.timeout || timeout);
      }
    };

    window.addEventListener(eventName, handler);
    return () => {
      window.removeEventListener(eventName, handler);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [standalone, eventName, timeout, autoClose, onClose]);

  const visible = show || self.show;

  // если standalone уже что-то записал в self.kind — берём его,
  // иначе используем проп kind (например, для confirm в карточке урока)
  const effKind = self.kind || kind;
  const effTitle = self.title || title;
  const effText = self.text || text;

  const yes = self.onYes || onYes;
  const no = self.onNo || onNo;

  const closeSelf = () => {
    setSelf((s) => ({ ...s, show: false }));
    onClose?.();
  };

  /* ================= inline-режим ================= */
  if (position === "inline") {
    if (!visible) return null;
    const isConfirm = effKind === "confirm";

    return (
      <div className={`wallet-toast ${effKind} inline`}>
        {icon && <div className="wallet-toast-icon">{icon}</div>}
        <div className="wallet-toast-body">
          {effTitle && <div className="wallet-toast-title">{effTitle}</div>}
          {effText && (
            <div className="wallet-toast-text wallet-toast-text--split">
              {effText}
            </div>
          )}

          {isConfirm ? (
            <div className="toast-actions">
              <button
                className="tutor-confirm-yes"
                onClick={(e) => {
                  e.stopPropagation();
                  yes?.();
                  closeSelf();
                }}
              >
                Да
              </button>
              <button
                className="tutor-confirm-no"
                onClick={(e) => {
                  e.stopPropagation();
                  if (no) {
                    no();
                  } else {
                    closeSelf();
                  }
                }}
              >
                Нет
              </button>
            </div>
          ) : (
            showButton && (
              <button
                className="tutor-confirm-ok"
                onClick={(e) => {
                  e.stopPropagation();
                  closeSelf();
                }}
              >
                {buttonText || "Ок"}
              </button>
            )
          )}
        </div>
      </div>
    );
  }

  /* ================= fixed-тост ================= */
  if (!visible) return null;

  const isConfirm = effKind === "confirm";
  const effShowButton =
    typeof self.showButton === "boolean" ? self.showButton : showButton;

  const hasOkButton = !isConfirm && effShowButton;

  const node = (
    <div
      className={`wallet-toast ${effKind}`}
      style={{
        position: "fixed",
        zIndex: 11000,
        // если есть кнопки — кликабельный, иначе "просматриваемый"
        pointerEvents: isConfirm || hasOkButton ? "auto" : "none",
        ...(style && (style.left || style.right)
          ? {}
          : { top: "1.2vw", right: "1.2vw" }),
        ...(style || {}),
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {icon && <div className="wallet-toast-icon">{icon}</div>}

      <div className="wallet-toast-body">
        {headline && <div className="wallet-toast-headline">{headline}</div>}
        {effTitle && <div className="wallet-toast-title">{effTitle}</div>}
        {effText && (
          <div className="wallet-toast-text wallet-toast-text--split">
            {effText}
          </div>
        )}

        {isConfirm ? (
          <div className="toast-actions">
            <button
              className="tutor-confirm-yes"
              onClick={(e) => {
                e.stopPropagation();
                yes?.();
                closeSelf();
              }}
            >
              Да
            </button>
            <button
              className="tutor-confirm-no"
              onClick={(e) => {
                e.stopPropagation();
                if (no) {
                  no();
                } else {
                  closeSelf();
                }
              }}
            >
              Нет
            </button>
          </div>
        ) : (
          hasOkButton && (
            <div className="toast-actions">
              <button
                className="tutor-confirm-ok"
                onClick={(e) => {
                  e.stopPropagation();
                  closeSelf();
                }}
              >
                {buttonText || "Ок"}
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );

  return usePortal ? createPortal(node, document.body) : node;
}
